import { useMemo } from 'react';

import { useQueryDataProductSpec } from 'pages/qa-spec/hooks/useProductSpecInfo';

import { isEmpty, get, set, isArray, isBoolean } from 'lodash';

import { useGetQaPackagingTabData } from '../qa-packaging/queries';
import { useGetIngredients } from '../qa-ingredient/hook';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';
import {
  useGetOrganolepticQuery,
  useGetQaSpecDataHeader,
  useContaminantCharacteristicsQuery,
  useGetPhysicalChemicalQuery,
  useGetMicrobiologicalCharacteristicsQuery,
  useGetQaSpecComponentQuery
} from 'pages/qa-spec/query';
import { useGetSpecAllergens } from 'pages/qa-spec/components/qa-allergen/hooks';
import { useGetQaPetFood } from 'pages/qa-spec/components/qa-spec-pet-food/hooks';
import { useGetQaClaimsTabData } from 'pages/qa-spec/components/qa-claims/queries';
import { useQueryQaFormulaData } from 'pages/qa-spec/components/qa-formula/queries';
import { useManufacturingStageData } from 'pages/qa-spec/components/qa-manufacturing-process/hooks';
import { useGetFreshDeli } from 'pages/qa-spec/components/qa-fresh-deli/hooks';
import { useGetQaFreshMeatTabData } from 'pages/qa-spec/components/qa-fresh-meat/queries';
import { useGetFreshProduce } from 'pages/qa-spec/components/qa-fresh-produce/hooks';
import { useGetQaFreshSeafoodTabData } from 'pages/qa-spec/components/qa-fresh-seafood/queries';
import { useQueryPetFoodPanel } from 'pages/product-full-view/components/product-media-area/multiple-panel/facts-panel/useQueryFactsPanel';
import {
  useQueryDrugFactsPanel,
  useQueryFactsPanel,
  useQuerySupplementFactsPanel
} from 'pages/qa-spec/components/qa-nutrition-data/hook';

import { filterRequiredFieldsGroupData } from './utils';
import { mappingRequiredFieldsForHbcOtc } from 'pages/home/ribbon/qas-require-field/components/utils';
import { useGetAllProductFields } from 'hooks/qas-require-field/useQasRequireField';

import { useGetQaProductContainTabData } from '../qa-product-contain/queries';

import {
  isNullOrUndefinedValue,
  transformInitialDataToFormValues
} from 'pages/qa-spec/utils/transformQaBooleanValue';

import { SUB_FIELDS_FOR_CHECKING } from 'pages/qa-spec/components/qa-spec-header-form/constants';
import {
  generateFormValuesClaims,
  mapClaimsEnumByValue
} from 'pages/qa-spec/components/qa-claims/utils';
import { useGetEnumsByEnumName } from 'hooks';
import { useGetPackagingMasterCaseQuery } from 'pages/qa-spec/query';

const ORGANOLEPTIC_PARAMETER_REQUIRED_FIELD_MAP = {
  'Aroma/Odor': 'Odor',
  'Flavor/Taste': 'Taste'
};

export const AT_LEAST_ONE_ENTRY_GROUPS = [
  'Physical & Chemical Characteristics',
  'Contaminant Characteristics',
  'Microbiological Characteristics'
];

export const getInvalidRequiredFields = ({ data, fields }) => {
  if (!fields) return {};
  if (isEmpty(data)) return fields;

  let result = {};

  if (fields?.length > 0) {
    for (const field of fields) {
      const fieldNameArray = field?.fieldFullPath?.split('.');
      const fieldName = fieldNameArray[fieldNameArray?.length - 1];
      const valueField = data[fieldName];
      const isNumberType = typeof valueField === 'number';
      const isNullOrUndefinedValue =
        valueField === null || valueField === undefined;

      if (Array.isArray(valueField) && valueField?.length === 0) {
        result = {
          ...result,
          [field]: true
        };
      } else if (isNullOrUndefinedValue && isNumberType) {
        result = {
          ...result,
          [field]: true
        };
      } else if (!valueField && !isNumberType) {
        result = {
          ...result,
          [field]: true
        };
      } else {
        result = {
          ...result,
          [field]: false
        };
      }
    }
  }

  return result;
};

export const useRequiredFieldsValidation = (productId) => {
  const { data: productFieldGroups } = useGetAllProductFields({});
  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId
  });
  const { data: requiredClaimFields } =
    useGetQaSpecRequiredFieldsInQaSpecFullView({
      productId
    });

  const { dataProductSpec } = useQueryDataProductSpec({
    id: productId
  });

  const { packagingData } = useGetPackagingMasterCaseQuery({
    productId,
    enabled: !!productId
  });

  const mappingDataProductSpec = useMemo(
    () => transformInitialDataToFormValues(dataProductSpec),
    [dataProductSpec]
  );

  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productId,
    isEnabled: Boolean(productId)
  });

  let requiredClaims = useGetEnumsByEnumName('OptionalClaims');
  requiredClaims = mapClaimsEnumByValue(requiredClaims);

  const qaType = qaSpecDataHeader?.type;

  const { organolepticData } = useGetOrganolepticQuery({
    productId,
    enabled: !!productId
  });

  const { contaminantCharacteristicsData } = useContaminantCharacteristicsQuery(
    {
      productId,
      enabled: !!productId
    }
  );

  const { physicalChemicalData } = useGetPhysicalChemicalQuery({
    productId,
    enabled: !!productId
  });
  const { microbiologicalCharacteristicData } =
    useGetMicrobiologicalCharacteristicsQuery({
      productId,
      enabled: !!productId
    });

  const { data: dataQaIngredient } = useGetIngredients(productId);

  const { qaAllergenList } = useGetSpecAllergens(productId);

  const { data: dataQaPackaging } = useGetQaPackagingTabData(
    {
      productId
    },
    {
      enabled: !!productId,
      retry: false
    }
  );

  // const { qaSpecDataContact } = useGetQaSpecDataContact({
  //   id: productId,
  //   isEnabled: !!productId,
  // });

  const { data: productContainsData } = useGetQaProductContainTabData(
    {
      productId
    },
    { enabled: !!productId }
  );

  const { componentData } = useGetQaSpecComponentQuery({
    productId,
    enabled: Number(productId)
  });

  const { data: petFoodData } = useGetQaPetFood(productId);

  const { data: claimsData } = useGetQaClaimsTabData(
    { productId, productType: qaType },
    { enabled: !!productId, retry: false }
  );

  const { data: qaFormulaData } = useQueryQaFormulaData({
    productId,
    productType: qaSpecDataHeader?.type,
    enabled: Boolean(productId)
  });

  const { data: freshDeliData } = useGetFreshDeli(productId);

  const { data: freshMeatData } = useGetQaFreshMeatTabData(
    { productId },
    { enabled: !!productId, retry: false }
  );

  const { data: freshProduceData } = useGetFreshProduce(productId);
  const { data: freshSeafoodData } = useGetQaFreshSeafoodTabData(
    { productId },
    { enabled: !!productId, retry: false }
  );

  const { listFactsPanel } = useQueryFactsPanel({
    id: productId
  });

  const { listSupplementFactsPanel } = useQuerySupplementFactsPanel({
    id: productId
  });

  const { listDrugFactsPanel } = useQueryDrugFactsPanel({
    id: productId
  });

  const { listPetFoodPanel } = useQueryPetFoodPanel({
    id: productId
  });

  const { rowData: manufacturingProcessData } =
    useManufacturingStageData(productId);

  const isNumber = (value) => {
    return typeof value === 'number';
  };

  const validateAllergenData = () => {
    return {
      ['allergenStatement'.toLowerCase(0)]: !!qaAllergenList?.allergenStatement,
      ...validateListTypeData(qaAllergenList?.allergensModel)
    };
  };

  const validateComponentData = () => {
    return {
      ['productClaims'.toLowerCase(0)]: !!componentData?.productClaims,
      ...validateListTypeData(componentData?.components)
    };
  };

  const checkClaimRequired = (fieldName) => {
    if (isEmpty(requiredClaimFields) || !fieldName) return false;

    const downCasedFieldName = fieldName.toLowerCase();

    const isNormalFieldRequired = requiredClaimFields.includes(
      `qaspecification.qaspecclaimsrequired.${downCasedFieldName}`
    );
    return isNormalFieldRequired;
  };

  const validateClaimsData = () => {
    let claimsObj = [];
    if (Array.isArray(claimsData?.claims)) {
      claimsObj.push(
        ...claimsData.claims.map((c) => ({
          claimTypeCode: requiredClaims[c.claimTypeCode]?.enumValue,
          isAttribute: c.isAttribute ?? null
        }))
      );
    }
    if (claimsData?.pfasClaim) {
      Object.entries(claimsData.pfasClaim).forEach(([key, value]) => {
        claimsObj.push({
          claimTypeCode: key,
          isAttribute: value != null ? value : null
        });
      });
    }

    claimsObj.push({
      claimTypeCode: 'contains',
      isAttribute:
        Array.isArray(claimsData?.contains) && claimsData.contains.length > 0
          ? true
          : null
    });
    claimsObj.push({
      claimTypeCode: 'doesNotContain',
      isAttribute:
        Array.isArray(claimsData?.doesNotContain) &&
        claimsData.doesNotContain.length > 0
          ? true
          : null
    });
    let result = {};
    for (const { claimTypeCode, isAttribute } of claimsObj) {
      if (!checkClaimRequired(claimTypeCode)) continue;

      result = {
        ...result,
        [claimTypeCode.toLowerCase()]: validateValue(isAttribute)
      };
    }
    return result;
  };

  const validateObjectTypeData = (data) => {
    // now, we changed to Radio that has null, No, Yes. If a field exist in list fields ana value parent of this field = No -> Ignore
    const subFieldsForChecking = Object.keys(SUB_FIELDS_FOR_CHECKING); //

    if (!data) return {};

    return Object.keys(data).reduce((accumulator, key) => {
      const value = data[key];

      if (subFieldsForChecking.includes(key)) {
        const parentField = SUB_FIELDS_FOR_CHECKING[key]?.parentField;
        const valueOfParentField = data[parentField];

        if (
          valueOfParentField === 'No' ||
          isNullOrUndefinedValue(valueOfParentField)
        ) {
          accumulator[key.toLowerCase()] = true;
        } else {
          accumulator[key.toLowerCase()] = validateValue(value);
        }
      } else {
        accumulator[key.toLowerCase()] = validateValue(value);
      }

      return accumulator;
    }, {});
  };

  const validateListTypeData = (dataList) => {
    if (!dataList?.length || !dataList[0]) return {};

    const dataItem = dataList[0];

    const data = {};

    Object.keys(dataItem).forEach((fieldName) => {
      const value = dataList.every((rowData) => {
        const fieldValue = rowData[fieldName];

        return validateValue(fieldValue);
      });

      set(data, [fieldName.toLowerCase()], value);
    });

    return data;
  };

  //* This is a combination of validateObjectTypeData data ane validateListTypeData for list type fields
  const validateListTypeField = ({ data, fieldMaps }) => {
    if (!data) return {};

    const result = validateObjectTypeData(data);

    Object.keys(fieldMaps).forEach((fieldName) => {
      const mapFieldName = fieldMaps[fieldName];

      const fieldData = data[fieldName];
      const fieldValue = validateListTypeData(fieldData);
      set(result, [mapFieldName.toLowerCase()], fieldValue);
    });

    return result;
  };

  //* fieldMaps = {dataFieldName, resultFieldName};
  const validateListTypeFieldInListTypeData = ({ dataList, fieldMaps }) => {
    if (!dataList?.length) return {};

    const dataItem = dataList[0];

    const result = validateListTypeField({ data: dataItem, fieldMaps });

    return result;
  };

  const validateValue = (value) => {
    //* validate number
    if (value === null) return false;
    if (value === undefined) return false;

    if (isArray(value)) return !!value?.length;
    if (isNumber(value)) return true;
    if (isBoolean(value)) return true;

    return !!value;
  };

  const validateOrganolepticData = () => {
    if (!organolepticData?.organolepticCharacteristics) return {};

    return organolepticData?.organolepticCharacteristics?.reduce(
      (accumulator, dataItem) => {
        Object.keys(dataItem).forEach((fieldName) => {
          const value = !!dataItem[fieldName];
          const parameter =
            ORGANOLEPTIC_PARAMETER_REQUIRED_FIELD_MAP[dataItem.parameter] ||
            dataItem.parameter;

          set(
            accumulator,
            [fieldName?.toLowerCase(), parameter?.toLowerCase()],
            value
          );
        });
        return accumulator;
      },
      {}
    );
  };

  const checkValidRequiredField = ({ field }) => {
    if (!validatedData) return false;
    const checkingPath = field?.validatePath
      ? field?.validatePath
      : field?.fieldFullPath;

    const value = get(validatedData, checkingPath?.toLowerCase());

    if (isArray(value)) return !!value?.length;

    return !!value;
  };

  const checkValidAllFields = (groups) => {
    return groups.every((groupItem) => {
      const isAllFieldValid = groupItem?.fields?.every((fieldItem) =>
        checkValidRequiredField({ field: fieldItem })
      );

      const isAllChildGroupValid =
        groupItem?.childGroups?.length > 0
          ? checkValidAllFields(groupItem?.childGroups)
          : true;

      return isAllFieldValid && isAllChildGroupValid;
    });
  };

  const validateEprPackagingData = () => {
    if (!packagingData) return {};

    const validateComponent = (data) => {
      if (!data) return {};
      return Array.isArray(data)
        ? validateListTypeData(data)
        : validateObjectTypeData(data);
    };

    return {
      mastercasecomponents: validateComponent(
        packagingData.masterCaseComponents
      ),
      retailerunitcomponents: validateComponent(
        packagingData.retailerUnitComponents
      )
    };
  };

  const validatedData = {
    ['qaSpecification'.toLowerCase()]: {
      ...validateObjectTypeData(qaSpecDataHeader),
      ['qaSpecProductSpecification'.toLowerCase()]: {
        ...validateObjectTypeData(mappingDataProductSpec),
        ['countryOfOrigin'.toLowerCase()]: validateListTypeData(
          mappingDataProductSpec?.countryOfOrigin
        ),
        ['nonfoodIngredientStatement'.toLowerCase()]: validateListTypeData(
          mappingDataProductSpec?.nonfoodIngredientStatement
        ),

        ['qaSpecProductSpecsOrganolepticCharacteristics'.toLowerCase()]:
          validateOrganolepticData(),

        ['qaSpecProductSpecsPhysicalAndChemicalCharacteristics'.toLowerCase()]:
          validateListTypeData(
            physicalChemicalData?.physicalAndChemicalCharacteristics
          ),
        ['qaSpecProductSpecsContaminantCharacteristics'.toLowerCase()]:
          validateListTypeData(
            contaminantCharacteristicsData?.contaminantCharacteristics
          ),
        ['qaSpecProductSpecsMicrobiologicalCharacteristics'.toLowerCase()]:
          validateListTypeData(
            microbiologicalCharacteristicData?.microbiologicalCharacteristics
          )
      },
      ['qaSpecIngredients'.toLowerCase()]: {
        ...validateObjectTypeData(dataQaIngredient),
        ['ingredientStatement'.toLowerCase()]:
          dataQaIngredient?.ingredientStatements?.length > 0,
        ...validateListTypeData(dataQaIngredient?.ingredients)
      },
      ['qaSpecAllergens'.toLowerCase()]: validateAllergenData(),
      ['bpComponents'.toLowerCase()]: validateComponentData(),
      ['qaSpecPackaging'.toLowerCase()]: {
        ...validateObjectTypeData(dataQaPackaging)
      },
      ['qaSpecProductContains'.toLowerCase()]:
        validateObjectTypeData(productContainsData),
      ['qaSpecPetFood'.toLowerCase()]: {
        ...validateObjectTypeData(petFoodData),
        geometries: validateListTypeData(petFoodData?.geometries)
      },
      ['qaSpecPetFood'.toLowerCase()]: {
        ...validateObjectTypeData(petFoodData),
        geometries: validateListTypeData(petFoodData?.geometries)
      },
      ['qaSpecFormula'.toLowerCase()]: {
        ...validateObjectTypeData(qaFormulaData),
        ...validateListTypeData(qaFormulaData?.formula)
      },
      ['qaSpecManufacturingProcesses'.toLowerCase()]: {
        ...validateListTypeData(manufacturingProcessData)
      },
      ['qaSpecClaims'.toLowerCase()]: validateClaimsData(),
      ['qaSpecFreshDeli'.toLowerCase()]: {
        ['qaSpecFreshDeliCheese'.toLocaleLowerCase()]: validateObjectTypeData(
          freshDeliData?.qaSpecFreshDeliCheese
        ),
        ['qaSpecFreshDeliMeat'.toLocaleLowerCase()]: validateObjectTypeData(
          freshDeliData?.qaSpecFreshDeliMeat
        )
      },
      ['qaSpecFreshMeat'.toLowerCase()]: validateObjectTypeData(freshMeatData),
      ['qaSpecFreshProduce'.toLowerCase()]:
        validateObjectTypeData(freshProduceData),
      ['qaSpecFreshSeafood'.toLowerCase()]:
        validateObjectTypeData(freshSeafoodData),
      ['factsPanel'.toLowerCase()]: {
        ['nutritionLabel'.toLowerCase()]: validateListTypeFieldInListTypeData({
          dataList: listFactsPanel,
          fieldMaps: { nutritionFactProperties: 'nutrients' }
        }),
        ['supplementLabel'.toLowerCase()]: validateListTypeFieldInListTypeData({
          dataList: listSupplementFactsPanel,
          fieldMaps: {
            nutrients: 'nutrients',
            dietaryIngredients: 'dietaryIngredients'
          }
        }),
        ['drugFactsLabel'.toLowerCase()]: {
          ...validateListTypeFieldInListTypeData({
            dataList: listDrugFactsPanel,
            fieldMaps: {
              directions: 'directions',
              activeIngredients: 'activeIngredients',
              otherInformation: 'otherInformation',
              warnings: 'warnings'
            }
          }),
          uses: validateObjectTypeData(listDrugFactsPanel?.[0]?.uses)
        },
        ['petNutritionLabel'.toLowerCase()]:
          validateListTypeFieldInListTypeData({
            dataList: listPetFoodPanel,
            fieldMaps: {
              nutrients: 'nutrients'
            }
          })
      },
      qaspeceprpackaging: validateEprPackagingData()
    }
  };
  console.log(
    'EPR validation:',
    validatedData.qaspecification.qaspeceprpackaging
  );

  const mappingFieldGroups = useMemo(() => {
    return mappingRequiredFieldsForHbcOtc(qaType, productFieldGroups);
  }, [productFieldGroups, qaType]);

  const groupRequiredFields = filterRequiredFieldsGroupData({
    groupsData: mappingFieldGroups,
    requiredFields,
    productType: qaType
  });

  const isAllFieldsValid = checkValidAllFields(groupRequiredFields);

  return { isAllFieldsValid, groupRequiredFields, checkValidRequiredField };
};
